import { useState, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  AlertTriangle,
  ArrowLeft,
  Eye,
  LifeBuoy,
  Loader2,
  Plus,
  RefreshCw,
  X,
} from 'lucide-react'
import { api, extractApiError } from '../../lib/api'
import { formatDateTime } from '../../lib/format'
import { useRedirectOnNotFound } from '../../lib/useRedirectOnNotFound'
import { Breadcrumb } from '../../components/Breadcrumb'
import { Button } from '../../components/ui/Button'
import { DataTable } from '../../components/table/DataTable'
import { RowActionsMenu } from '../../components/table/RowActionsMenu'
import {
  servexaTicket,
  servexaTicketResult,
  servexaTicketsResponse,
  SERVEXA_TICKET_PRIORITIES,
  SERVEXA_TICKET_STATUSES,
  SERVEXA_TICKET_TYPES,
} from '../../lib/types'
import { descriptionSchema, subjectTextSchema } from '../../lib/validators'
import { notSentBanner, notSentNotice } from '../../lib/servexaSync'

// Shared validators — same Subject (5–100) / Description (20–1000) rules the
// rest of the app uses. Built once at module load, exactly like SupportPage.
const SUBJECT_SCHEMA = subjectTextSchema()
const DESCRIPTION_SCHEMA = descriptionSchema()

// Servexa owns the workflow once a ticket is submitted (spec A1/A17). We NEVER
// show Servexa's internal stage names — the folded status codes (A13.1) map to
// friendly, customer-facing labels here.
const STATUS_LABEL = {
  NEW: 'Received',
  ASSIGNED: 'Assigned',
  IN_PROGRESS: 'In progress',
  PENDING_REVIEW: 'In review',
  // Not "Reopened": REWORK is an internal re-review step. A genuinely reopened
  // ticket arrives as Assigned / In progress again (spec A6 lifecycle).
  REWORK: 'Rework',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
}

const STATUS_TONE = {
  NEW: 'bg-[color:var(--color-info-bg)] text-[color:var(--color-info)] border border-[color:var(--color-info-border)]',
  ASSIGNED:
    'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]',
  IN_PROGRESS:
    'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]',
  // Amber "in review / sent back" — deliberately distinct from the blue/cyan
  // active states so a ticket awaiting the customer reads at a glance.
  PENDING_REVIEW:
    'bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border border-[color:var(--color-warning-border)]',
  REWORK:
    'bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border border-[color:var(--color-warning-border)]',
  RESOLVED:
    'bg-[color:var(--color-success-bg)] text-[color:var(--color-success)] border border-[color:var(--color-success-border)]',
  CLOSED:
    'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]',
}

// Servexa's wire enums → friendly labels. Kept keyed off the exported enum
// arrays so the <select> options and the response labels can't drift.
const TYPE_LABEL = {
  BUG: 'Bug',
  SUPPORT: 'Support',
  INCIDENT: 'Incident',
  QUESTION: 'Question',
  CHANGE_REQUEST: 'Change request',
}

const PRIORITY_LABEL = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  CRITICAL: 'Critical',
}

const PRIORITY_TONE = {
  CRITICAL: 'text-[color:var(--color-priority-urgent)]',
  HIGH: 'text-[color:var(--color-priority-high)]',
  MEDIUM: 'text-[color:var(--color-priority-normal)]',
  LOW: 'text-[color:var(--color-priority-low)]',
}

/**
 * Tenant "Support Tickets" page backed by the Servexa integration (spec A6).
 * A simpler sibling of the legacy SupportPage: list / create / view / retry
 * only. There is no in-app conversation — once a ticket is submitted Servexa
 * owns the workflow, so this page is a read-only status window plus a retry
 * affordance for tickets that never reached Servexa (spec A12).
 */
export function ServexaSupportPage() {
  // `/servexa-support/:ticketId` deep-links straight onto a ticket. Seed the
  // open ticket from the URL param and keep it in sync if the param changes
  // while the page is already mounted (mirrors SupportPage exactly).
  const { ticketId } = useParams()
  const [statusFilter, setStatusFilter] = useState('all')
  const [openId, setOpenId] = useState(ticketId ?? null)
  const [composing, setComposing] = useState(false)

  useEffect(() => {
    if (ticketId) setOpenId(ticketId)
  }, [ticketId])

  // One unfiltered fetch; the status tabs slice CLIENT-side so the per-status
  // counts always reconcile with the rows below them.
  const ticketsQuery = useQuery({
    queryKey: ['servexa-tickets'],
    queryFn: async () => {
      const res = await api.get('/servexa/tickets')
      return servexaTicketsResponse.parse(res.data)
    },
  })

  const allRows = useMemo(
    () => ticketsQuery.data?.data ?? [],
    [ticketsQuery.data],
  )
  const countByStatus = useMemo(() => {
    const map = { all: allRows.length }
    for (const t of allRows) map[t.status] = (map[t.status] ?? 0) + 1
    return map
  }, [allRows])
  const rows = useMemo(
    () =>
      statusFilter === 'all'
        ? allRows
        : allRows.filter((t) => t.status === statusFilter),
    [allRows, statusFilter],
  )

  const columns = useMemo(
    () => [
      {
        key: 'ticketNumber',
        header: 'Ticket',
        minWidth: 110,
        sortAccessor: (t) => t.ticketNumber ?? '',
        exportValue: (t) => t.ticketNumber ?? '',
        render: (t) => (
          <span className="text-xs font-semibold text-[color:var(--color-ink-muted)]">
            {t.ticketNumber ?? '—'}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        minWidth: 140,
        sortAccessor: (t) => t.status ?? '',
        exportValue: (t) => STATUS_LABEL[t.status] ?? t.status,
        render: (t) => (
          <div className="flex flex-col items-start gap-1">
            <StatusChip status={t.status} />
            {t.syncState !== 'synced' ? (
              // Subtle "hasn't reached Servexa yet" hint (spec A12). The full
              // warning + Retry lives on the detail.
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[color:var(--color-warning)]">
                <AlertTriangle size={10} /> Not sent
              </span>
            ) : null}
          </div>
        ),
      },
      {
        key: 'title',
        header: 'Subject',
        grow: true,
        pinnable: true,
        minWidth: 240,
        sortAccessor: (t) => (t.title ?? '').toLowerCase(),
        searchAccessor: (t) =>
          `${t.title ?? ''} ${t.description ?? ''} ${t.ticketNumber ?? ''}`,
        exportValue: (t) => t.title,
        render: (t) => (
          <div className="min-w-0">
            <p className="text-sm font-medium text-[color:var(--color-ink-strong)] truncate">
              {t.title}
            </p>
            <p className="text-xs text-[color:var(--color-ink-muted)] truncate">
              {t.description}
            </p>
          </div>
        ),
      },
      {
        key: 'type',
        header: 'Type',
        minWidth: 140,
        sortAccessor: (t) => t.type ?? '',
        exportValue: (t) => TYPE_LABEL[t.type] ?? t.type,
        render: (t) => (
          <span className="text-xs text-[color:var(--color-ink-muted)]">
            {TYPE_LABEL[t.type] ?? t.type}
          </span>
        ),
      },
      {
        key: 'priority',
        header: 'Priority',
        minWidth: 110,
        sortAccessor: (t) => t.priority ?? '',
        exportValue: (t) => PRIORITY_LABEL[t.priority] ?? t.priority,
        render: (t) => (
          <span
            className={
              'text-xs uppercase tracking-wider font-semibold ' +
              (PRIORITY_TONE[t.priority] ?? '')
            }
          >
            {PRIORITY_LABEL[t.priority] ?? t.priority}
          </span>
        ),
      },
      {
        key: 'updatedAt',
        header: 'Updated',
        minWidth: 160,
        sortAccessor: (t) => (t.updatedAt ? new Date(t.updatedAt) : null),
        exportValue: (t) => formatDateTime(t.updatedAt),
        render: (t) => (
          <span className="text-xs text-[color:var(--color-ink-muted)] whitespace-nowrap">
            {formatDateTime(t.updatedAt)}
          </span>
        ),
      },
      {
        key: 'actions',
        // Singular: this column renders exactly ONE row action.
        header: 'Action',
        pinnedRight: true,
        align: 'center',
        minWidth: 72,
        sortable: false,
        exportable: false,
        hideable: false,
        render: (t) => (
          <RowActionsMenu
            label={`Actions for ${t.title}`}
            items={[
              {
                key: 'view',
                label: 'View ticket',
                icon: Eye,
                onClick: () => setOpenId(t.id),
              },
            ]}
          />
        ),
      },
    ],
    [],
  )

  if (openId) {
    return <TicketDetail ticketId={openId} onBack={() => setOpenId(null)} />
  }

  const statusTabs = ['all', ...SERVEXA_TICKET_STATUSES]

  return (
    <div>
      <Breadcrumb current="Support Tickets" />
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
            <LifeBuoy size={20} className="text-[color:var(--color-accent-strong)]" />
            Support Tickets
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            Raise a support ticket and track its status with our team.
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={() => setComposing(true)}>
          <Plus size={14} />
          New Ticket
        </Button>
      </div>

      {/* Status pills — client-side slice with per-status counts in the label */}
      <div className="flex flex-wrap items-center gap-1 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-0.5 w-fit mb-4">
        {statusTabs.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={
              'px-3 py-1.5 rounded text-xs font-medium cursor-pointer ' +
              (statusFilter === s
                ? 'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-strong)] shadow-sm'
                : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)]')
            }
          >
            {`${s === 'all' ? 'All' : STATUS_LABEL[s]} (${countByStatus[s] ?? 0})`}
          </button>
        ))}
      </div>

      {ticketsQuery.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)]">Loading…</p>
      ) : ticketsQuery.isError ? (
        // Surface the real error — without this a 403/500 would fall into
        // `rows = []` and masquerade as the empty state.
        <p className="text-sm text-[color:var(--color-priority-urgent)]">
          {extractApiError(ticketsQuery.error).message}
        </p>
      ) : rows.length === 0 ? (
        <EmptyState
          statusFilter={statusFilter}
          onCreate={() => setComposing(true)}
          onClearFilter={() => setStatusFilter('all')}
        />
      ) : (
        <DataTable
          tableId="app-servexa-tickets"
          columns={columns}
          rows={rows}
          getRowId={(t) => t.id}
          title="Support Tickets"
          exportBaseName="support-tickets"
          exportTitle="Support Tickets"
          selectable
          searchPlaceholder="Search by Subject or Ticket ID"
          defaultSort={[{ key: 'updatedAt', dir: 'desc' }]}
          emptyState={{
            icon: LifeBuoy,
            title: 'No tickets match your search',
            body: 'Try a different search term, or clear it to see all tickets in this status.',
          }}
        />
      )}

      {composing ? <ComposeDialog onClose={() => setComposing(false)} /> : null}
    </div>
  )
}

/** Ticket status chip for the Support list Status column. */
function StatusChip({ status }) {
  return (
    <span
      className={
        'text-xs uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded w-fit inline-block ' +
        (STATUS_TONE[status] ?? '')
      }
    >
      {STATUS_LABEL[status] ?? status}
    </span>
  )
}

function EmptyState({ statusFilter, onCreate, onClearFilter }) {
  // A specific status tab with no matches — tickets may exist under other
  // tabs, so DON'T show the first-time prompt. Context-aware copy instead.
  if (statusFilter && statusFilter !== 'all') {
    return (
      <div className="rounded-2xl border border-dashed border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-12 text-center">
        <LifeBuoy size={28} className="mx-auto mb-2 text-[color:var(--color-ink-subtle)]" />
        <p className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
          No {STATUS_LABEL[statusFilter]?.toLowerCase() ?? ''} tickets
        </p>
        <p className="mt-1 text-xs text-[color:var(--color-ink-muted)] max-w-md mx-auto">
          No tickets have this status right now. Check another tab to see your
          other tickets.
        </p>
        <button
          type="button"
          onClick={onClearFilter}
          className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold text-[color:var(--color-accent-strong)] hover:underline cursor-pointer"
        >
          Show All Tickets
        </button>
      </div>
    )
  }
  // Genuine first-time empty — the All tab with no tickets at all.
  return (
    <div className="rounded-2xl border border-dashed border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-12 text-center">
      <LifeBuoy size={28} className="mx-auto mb-2 text-[color:var(--color-ink-subtle)]" />
      <p className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
        No support tickets yet
      </p>
      <p className="mt-1 text-xs text-[color:var(--color-ink-muted)] max-w-md mx-auto">
        File a ticket if anything's broken, missing, or unclear. Our support
        team will pick it up and keep the status updated here.
      </p>
      <button
        type="button"
        onClick={onCreate}
        className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer"
      >
        <Plus size={14} />
        File a Ticket
      </button>
    </div>
  )
}

function TicketDetail({ ticketId, onBack }) {
  const qc = useQueryClient()

  const detailQuery = useQuery({
    queryKey: ['servexa-ticket', ticketId],
    queryFn: async () => {
      const res = await api.get(`/servexa/tickets/${ticketId}`)
      return servexaTicket.parse(res.data)
    },
  })

  // Don't strand the viewer on a dead "not found" page when the ticket URL is
  // stale — return to the list with a notice.
  useRedirectOnNotFound(detailQuery, onBack, 'This ticket is no longer available.')

  const retryMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/servexa/tickets/${ticketId}/retry`)
      return servexaTicketResult.parse(res.data)
    },
    onSuccess: (result) => {
      if (result.submitted) {
        toast.success(
          result.ticketNumber
            ? `Ticket sent to support — reference ${result.ticketNumber}.`
            : 'Ticket sent to support.',
        )
      } else {
        // Queued vs could-not-be-sent read differently (spec A12/A22).
        const notice = notSentNotice(result, 'Ticket')
        ;(notice.tone === 'warning' ? toast.warning : toast.message)(notice.text)
      }
      void qc.invalidateQueries({ queryKey: ['servexa-tickets'] })
      void qc.invalidateQueries({ queryKey: ['servexa-ticket', ticketId] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  if (detailQuery.isLoading) {
    return (
      <div>
        <Breadcrumb current="Support Tickets" />
        <div className="grid place-items-center py-24">
          <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading ticket…
          </p>
        </div>
      </div>
    )
  }

  const t = detailQuery.data
  if (!t) {
    return (
      <div>
        <Breadcrumb current="Support Tickets" />
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-[color:var(--color-accent-strong)] hover:underline inline-flex items-center gap-1 mb-4"
        >
          <ArrowLeft size={14} /> Back to Tickets
        </button>
        <p className="text-sm text-[color:var(--color-priority-urgent)]">
          {detailQuery.isError
            ? extractApiError(detailQuery.error).message
            : 'Ticket not found.'}
        </p>
      </div>
    )
  }

  const notSent = t.syncState === 'pending_sync' || t.syncState === 'failed'

  return (
    <div>
      <Breadcrumb current="Support Tickets" />
      <button
        type="button"
        onClick={onBack}
        className="text-sm text-[color:var(--color-accent-strong)] hover:underline inline-flex items-center gap-1 mb-4"
      >
        <ArrowLeft size={14} /> Back to Tickets
      </button>

      <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="font-display text-2xl text-[color:var(--color-ink-strong)] break-words [overflow-wrap:anywhere]">
              {t.title}
            </h1>
            <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
              Filed {formatDateTime(t.createdAt)} · Last update{' '}
              {formatDateTime(t.updatedAt)}
            </p>
          </div>
          <div className="shrink-0">
            <StatusChip status={t.status} />
          </div>
        </div>

        <p className="mt-4 text-sm text-[color:var(--color-ink)] whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
          {t.description}
        </p>

        {/* Read-only facts — never Servexa's internal workflow names. */}
        <dl className="mt-6 grid grid-cols-1 gap-4 border-t border-[color:var(--color-rule)] pt-4 sm:grid-cols-3">
          <div>
            <dt className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
              Ticket number
            </dt>
            <dd className="mt-1 text-sm text-[color:var(--color-ink-strong)]">
              {t.ticketNumber ?? 'Pending'}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
              Type
            </dt>
            <dd className="mt-1 text-sm text-[color:var(--color-ink-strong)]">
              {TYPE_LABEL[t.type] ?? t.type}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
              Priority
            </dt>
            <dd className="mt-1 text-sm text-[color:var(--color-ink-strong)]">
              {PRIORITY_LABEL[t.priority] ?? t.priority}
            </dd>
          </div>
        </dl>

        {/* Sync warning (spec A12) — the ticket hasn't reached Servexa yet, so
         *  offer a retry. Synced tickets show no retry affordance. */}
        {notSent ? (
          <div className="mt-6 rounded-lg border border-[color:var(--color-warning-border)] bg-[color:var(--color-warning-bg)] p-4">
            <div className="flex items-start gap-2.5">
              <AlertTriangle
                size={16}
                className="mt-0.5 shrink-0 text-[color:var(--color-warning)]"
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-[color:var(--color-warning)]">
                  {notSentBanner(t).title}
                </p>
                <p className="mt-1 text-sm text-[color:var(--color-ink)] break-words [overflow-wrap:anywhere]">
                  {notSentBanner(t).body}
                </p>
                <div className="mt-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => retryMutation.mutate()}
                    loading={retryMutation.isPending}
                  >
                    <RefreshCw size={14} />
                    Retry
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <p className="mt-4 text-sm text-[color:var(--color-ink-muted)] italic">
          Our support team will update the status here.
        </p>
      </div>
    </div>
  )
}

function ComposeDialog({ onClose }) {
  const qc = useQueryClient()
  // Dismissal model matches the shared Modal: Escape / X / Cancel close; a
  // backdrop click deliberately does NOT — this is a form, and a stray click
  // outside shouldn't discard what the user typed.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const [form, setForm] = useState({
    title: '',
    description: '',
    type: 'SUPPORT',
    priority: 'MEDIUM',
  })
  const [errors, setErrors] = useState({})

  // One source of truth for the field rules so validate + submit stay in sync
  // with the shared validators.
  function validateField(field, value) {
    const schema = field === 'title' ? SUBJECT_SCHEMA : DESCRIPTION_SCHEMA
    const r = schema.safeParse(value)
    return r.success ? undefined : r.error.issues[0]?.message
  }
  function setField(field, value) {
    // Strip leading whitespace as the user types (trailing is reaped on blur).
    const next =
      (field === 'title' || field === 'description') && typeof value === 'string'
        ? value.replace(/^\s+/, '')
        : value
    setForm((f) => ({ ...f, [field]: next }))
    // Re-validate live once an error is showing; surface a MAX-length error the
    // instant the cap is exceeded. Minimum-length errors stay deferred to blur.
    if (field === 'title' || field === 'description') {
      const msg = validateField(field, next)
      if (errors[field] || (msg && /at most/.test(msg))) {
        setErrors((e) => ({ ...e, [field]: msg }))
      }
    }
  }
  function trimFieldOnBlur(field) {
    setForm((f) => ({
      ...f,
      [field]:
        typeof f[field] === 'string' ? f[field].replace(/\s+$/, '') : f[field],
    }))
  }
  function validateAll() {
    const next = {
      title: validateField('title', form.title),
      description: validateField('description', form.description),
    }
    setErrors(next)
    return !next.title && !next.description
  }

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!validateAll()) {
        throw new Error('validation_failed')
      }
      const res = await api.post('/servexa/tickets', {
        title: form.title,
        description: form.description,
        type: form.type,
        priority: form.priority,
      })
      return servexaTicketResult.parse(res.data)
    },
    onSuccess: (result) => {
      if (result.submitted) {
        toast.success(
          result.ticketNumber
            ? `Ticket created — reference ${result.ticketNumber}.`
            : 'Ticket created and sent to support.',
        )
      } else {
        // Saved but not sent: say whether it is queued or needs a retry.
        const notice = notSentNotice(result, 'Ticket')
        ;(notice.tone === 'warning' ? toast.warning : toast.message)(notice.text)
      }
      void qc.invalidateQueries({ queryKey: ['servexa-tickets'] })
      onClose()
    },
    onError: (err) => {
      if (err?.message === 'validation_failed') return
      toast.error(extractApiError(err).message)
    },
  })

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4">
      <div className="w-[520px] rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl">
        <div className="px-5 py-4 border-b border-[color:var(--color-rule)] flex items-center justify-between">
          <p className="font-semibold text-[color:var(--color-ink-strong)]">
            File a support ticket
          </p>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-[color:var(--color-card-hover)] cursor-pointer"
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1">
              Subject<span className="text-[color:var(--color-priority-urgent)] ml-0.5">*</span>
            </label>
            <input
              value={form.title}
              onChange={(e) => setField('title', e.target.value)}
              onBlur={() => {
                trimFieldOnBlur('title')
                setErrors((e) => ({
                  ...e,
                  title: validateField('title', form.title.replace(/\s+$/, '')),
                }))
              }}
              placeholder="Enter a Subject"
              className={
                'w-full h-10 px-3 rounded-md border bg-[color:var(--color-canvas)] outline-none focus:border-[color:var(--color-accent)] ' +
                (errors.title
                  ? 'border-[color:var(--color-priority-urgent)]'
                  : 'border-[color:var(--color-rule)]')
              }
            />
            {errors.title ? (
              <p role="alert" className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
                {errors.title}
              </p>
            ) : null}
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1">
              Description<span className="text-[color:var(--color-priority-urgent)] ml-0.5">*</span>
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              onBlur={() => {
                trimFieldOnBlur('description')
                setErrors((e) => ({
                  ...e,
                  description: validateField(
                    'description',
                    form.description.replace(/\s+$/, ''),
                  ),
                }))
              }}
              rows={5}
              placeholder="Enter a Description"
              className={
                'w-full px-3 py-2 rounded-md border bg-[color:var(--color-canvas)] outline-none focus:border-[color:var(--color-accent)] resize-y text-sm ' +
                (errors.description
                  ? 'border-[color:var(--color-priority-urgent)]'
                  : 'border-[color:var(--color-rule)]')
              }
            />
            {errors.description ? (
              <p role="alert" className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
                {errors.description}
              </p>
            ) : null}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1">
                Type
              </label>
              <select
                value={form.type}
                onChange={(e) => setField('type', e.target.value)}
                className="w-full h-10 px-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer"
              >
                {SERVEXA_TICKET_TYPES.map((v) => (
                  <option key={v} value={v}>
                    {TYPE_LABEL[v] ?? v}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1">
                Priority
              </label>
              <select
                value={form.priority}
                onChange={(e) => setField('priority', e.target.value)}
                className="w-full h-10 px-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer"
              >
                {SERVEXA_TICKET_PRIORITIES.map((v) => (
                  <option key={v} value={v}>
                    {PRIORITY_LABEL[v] ?? v}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-[color:var(--color-rule)] flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded text-sm border border-[color:var(--color-rule)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
          >
            Cancel
          </button>
          {/* Kept enabled on empty fields (only disabled mid-submit) so a click
           *  runs `validateAll` and surfaces the required-field errors instead
           *  of a dead, greyed-out button. */}
          <button
            type="button"
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending}
            className="px-3 py-1.5 rounded text-sm font-semibold bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {createMutation.isPending ? 'Filing…' : 'File Ticket'}
          </button>
        </div>
      </div>
    </div>
  )
}
